/**
 * Why a parameter row of a parameter-plan response looks the way it does
 * (wire reason, Go resourceplan.ParameterReason, agion/Agion#2668). The
 * server decides the code; the designer only words it. Reasons are response
 * data: they never enter the plan's validation or the saved plan.
 */
export type ResourceParameterReasonCode =
  /** The connection selects several libraries or lists and the step names none. */
  | 'choose_from_selection'
  /** The step holds a value the connection's settings refuse. */
  | 'not_in_selection'
  /** The connection selects no library or list; it changes no verdict. */
  | 'no_selection'
  /** The host fills this input from the connection. */
  | 'host_supplied';

/** A value the connection admits, with the name it stores, if any. */
export interface ResourceParameterAllowedOption {
  value: string;
  label?: string;
}

export interface ResourceParameterReason {
  code: ResourceParameterReasonCode;
  /** Present for choose_from_selection and not_in_selection. */
  params?: { allowed: ResourceParameterAllowedOption[] };
}

const REASON_CODES: ReadonlySet<string> = new Set<ResourceParameterReasonCode>([
  'choose_from_selection',
  'not_in_selection',
  'no_selection',
  'host_supplied',
]);

/**
 * The reason a row carries, or undefined. A code this client does not know is
 * dropped, since the field is additive and a newer server may send one.
 */
export function normalizeResourceParameterReason(raw: unknown): ResourceParameterReason | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const { code, params } = raw as { code?: unknown; params?: unknown };
  if (typeof code !== 'string' || !REASON_CODES.has(code)) return undefined;
  const allowed = params && typeof params === 'object' ? (params as { allowed?: unknown }).allowed : undefined;
  if (!Array.isArray(allowed)) return { code: code as ResourceParameterReasonCode };
  return {
    code: code as ResourceParameterReasonCode,
    params: {
      allowed: allowed
        .filter((option): option is { value: string; label?: unknown } =>
          !!option && typeof option === 'object' && typeof (option as { value?: unknown }).value === 'string')
        .map((option) => (typeof option.label === 'string' && option.label
          ? { value: option.value, label: option.label }
          : { value: option.value })),
    },
  };
}
